// src/components/TeamItemsPage/TaskTemplatesModal.jsx

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styles from './TaskTemplatesModal.module.css';
import { FaTimes, FaStar, FaPlus, FaTrash } from 'react-icons/fa';

const DEFAULT_TEMPLATES = [
  {
    id: 'template-1',
    title: 'Pre-Game Equipment Check',
    description: 'Verify all equipment is ready before the game',
    priority: 'High',
    tags: ['equipment', 'pre-game'],
    category: 'Game Day'
  },
  {
    id: 'template-2',
    title: 'Post-Game Cleanup',
    description: 'Clean up field and collect equipment after the game',
    priority: 'Medium',
    tags: ['cleanup', 'post-game'],
    category: 'Game Day'
  },
  {
    id: 'template-3',
    title: 'Weekly Attendance Report',
    description: 'Compile and send weekly attendance report',
    priority: 'Low',
    tags: ['admin', 'reporting'],
    category: 'Administrative'
  },
  {
    id: 'template-4',
    title: 'Field Setup',
    description: 'Set up cones, goals, and other field equipment',
    priority: 'High',
    tags: ['equipment', 'setup'],
    category: 'Practice'
  },
  {
    id: 'template-5',
    title: 'Parent Communication',
    description: 'Send update to parents about upcoming events',
    priority: 'Medium',
    tags: ['communication', 'parents'],
    category: 'Administrative'
  }
];

const TaskTemplatesModal = ({ isOpen, onClose, onSelectTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    // Load templates from localStorage
    const stored = localStorage.getItem('assignmentTemplates');
    if (stored) {
      setTemplates(JSON.parse(stored));
    } else {
      setTemplates(DEFAULT_TEMPLATES);
      localStorage.setItem('assignmentTemplates', JSON.stringify(DEFAULT_TEMPLATES));
    }
  }, []);

  const categories = ['all', ...new Set(templates.map(t => t.category))];

  const filteredTemplates = selectedCategory === 'all'
    ? templates
    : templates.filter(t => t.category === selectedCategory);

  const handleDeleteTemplate = (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      const updated = templates.filter(t => t.id !== templateId);
      setTemplates(updated);
      localStorage.setItem('assignmentTemplates', JSON.stringify(updated));
    }
  };

  const handleSelectTemplate = (template) => {
    // Remove id from template before using
    const { id, category, ...templateData } = template;
    onSelectTemplate(templateData);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FaStar className={styles.titleIcon} />
            Assignment Templates
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Category Filter */}
          <div className={styles.categoryFilter}>
            {categories.map(category => (
              <button
                key={category}
                className={`${styles.categoryBtn} ${selectedCategory === category ? styles.categoryBtnActive : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>

          {/* Templates Grid */}
          <div className={styles.templatesGrid}>
            {filteredTemplates.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No templates found</p>
              </div>
            ) : (
              filteredTemplates.map(template => (
                <div key={template.id} className={styles.templateCard}>
                  <div className={styles.templateHeader}>
                    <h3 className={styles.templateTitle}>{template.title}</h3>
                    <div className={styles.templateActions}>
                      <button
                        className={styles.deleteTemplateBtn}
                        onClick={() => handleDeleteTemplate(template.id)}
                        title="Delete template"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  
                  {template.description && (
                    <p className={styles.templateDescription}>{template.description}</p>
                  )}

                  <div className={styles.templateMeta}>
                    <span className={`${styles.priorityBadge} ${styles['priority' + template.priority]}`}>
                      {template.priority}
                    </span>
                    {template.category && (
                      <span className={styles.categoryBadge}>{template.category}</span>
                    )}
                  </div>

                  {template.tags && template.tags.length > 0 && (
                    <div className={styles.templateTags}>
                      {template.tags.map((tag, index) => (
                        <span key={index} className={styles.tag}>{tag}</span>
                      ))}
                    </div>
                  )}

                  <button
                    className={styles.useTemplateBtn}
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <FaPlus /> Use Template
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

TaskTemplatesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectTemplate: PropTypes.func.isRequired
};

export default TaskTemplatesModal;

